import { useRef, useState } from "react";
import styles from "./list.module.css";
import dayjs from "dayjs";
import { Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Checkbox, Dialog } from "@mui/material";
import { FileDownloadOutlined, PersonAddAltOutlined, PrintOutlined, AddOutlined } from "@mui/icons-material";
import { Inbox } from "react-feather";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import Loader from "../../../components/Loader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import { ComponentToPrintList } from "./ComponentToPrintList";
import { useReactToPrint } from "react-to-print";
import AddDriverForm from "../../../components/popups/AddDriverForm";
import AssignPopup from "../../../components/popups/AssignBookingDriverPopup";
import ServiceTag from "../../../components/ServiceTag";
import ListToolbar from "../../../components/ListToolbar/ListToolbar";
import { downloadCsv } from "../../../components/ListToolbar/csv";

// Bookings waiting for a driver: international collections and local deliveries (tagged per row)
export const EXPORT_COLUMNS = [
  { label: "Booking Id", value: (row) => row?.booking_id },
  { label: "Service", value: (row) => (parseInt(row?.is_delivery) === 1 ? "Shop Order" : row?.freight_type || "Sea Freight") },
  { label: "Post Code", value: (row) => row?.post_code },
  { label: "Customer Name", value: (row) => row?.customer_name },
  { label: "Address", value: (row) => row?.pickup_details?.address },
  { label: "Order Status", value: (row) => (parseInt(row?.payment_status) === 1 ? "Paid" : "Unpaid") },
  { label: "Contact No", value: (row) => row?.pickup_details?.phone },
  { label: "Date", value: (row) => dayjs(row?.pickup_date).format("DD/MM/YYYY") },
];

const BookingList = ({ selected, filteredData, filterVars, driverList, setSelected, setFilterVars, setAllData, setSnackbarOpen, setSnackbarMessage, setSnackbarType, page, setPage, rowsPerPage, setRowsPerPage, totalCount, onAssign, viewSwitch }) => {
  const auth = authData();
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [driverFormOpen, setDriverFormOpen] = useState(false);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  const canAssign = auth?.user_type === 2 || auth?.permissions?.indexOf('bookings:assign-driver') > -1;

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const selectRowEvent = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const assignDrivers = () => {
    if (selected.length === 0) {
      setSnackbarType("error");
      setSnackbarOpen(true);
      setSnackbarMessage("Please select booking");
      return false;
    }
    setFormOpen(true);
  };


  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <ListToolbar
      search={filterVars?.searchKey}
      onSearch={(value) => setFilterVars((prev) => ({ ...prev, searchKey: value }))}
      dateRange={filterVars?.date}
      onDateRange={(value) => setFilterVars((prev) => ({ ...prev, date: value }))}
      viewSwitch={viewSwitch}
      selectedCount={selected.length}
      onClearSelection={() => setSelected([])}
      primaryAction={canAssign ? { label: selected.length ? `Assign to driver (${selected.length})` : "Assign to driver", icon: <PersonAddAltOutlined />, onClick: assignDrivers, disabled: selected.length === 0, hint: "Tick the bookings to assign first" } : null}
      menuItems={[
        canAssign && { label: "Add driver", icon: <AddOutlined fontSize="small" />, onClick: () => setDriverFormOpen(true) },
        { label: "Export CSV", icon: <FileDownloadOutlined fontSize="small" />, onClick: () => downloadCsv("assign-bookings.csv", EXPORT_COLUMNS, filteredData), disabled: !filteredData?.length },
        { label: "Print bookings", icon: <PrintOutlined fontSize="small" />, onClick: handlePrint, disabled: !filteredData?.length },
      ]}
    />
    <div style={{ display: "none" }}>
      <ComponentToPrintList ref={printRef} data={filteredData} />
    </div>

    <div className={`${styles.CusTable}`}>
      <TableContainer component={Paper} className={styles.ResponsiveTableWrap}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {canAssign && <StyledMainTableCell padding="checkbox">
                <Checkbox
                  className={`${styles.AllCheckbox}`}
                  color="primary"
                  indeterminate={selected?.length > 0 && selected?.length < filteredData?.length}
                  checked={filteredData?.length > 0 && selected?.length === filteredData?.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      const newSelected = filteredData.map((n) => n.id);
                      setSelected(newSelected);
                      return;
                    }
                    setSelected([]);
                  }}
                />
              </StyledMainTableCell>}
              <StyledMainTableCell>Booking Id</StyledMainTableCell>
              <StyledMainTableCell>Post Code</StyledMainTableCell>
              <StyledMainTableCell>Customer Name</StyledMainTableCell>
              <StyledMainTableCell>Address</StyledMainTableCell>
              <StyledMainTableCell>Order Status</StyledMainTableCell>
              <StyledMainTableCell>Contact No</StyledMainTableCell>
              <StyledMainTableCell>Date</StyledMainTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredData.map((item) => {
              const isItemSelected = selected.indexOf(item?.id) !== -1;

              return (<StyledMainTableRow key={item?.id} className={styles.ResponsiveRow} sx={{ "&:last-child td, &:last-child th": { border: 0, cursor: "pointer" } }} onClick={(e) => selectRowEvent(item?.id)} aria-checked={isItemSelected} selected={isItemSelected}>
                {canAssign && <TableCell padding="checkbox" className={styles.CheckboxCell}>
                  <Checkbox color="primary" checked={isItemSelected} />
                </TableCell>}
                <TableCell component="th" scope="row" data-label="Booking Id" className={styles.Mono}>{item?.booking_id}<ServiceTag booking={item} /></TableCell>
                <TableCell component="th" scope="row" data-label="Post Code">{item?.post_code}</TableCell>
                <TableCell component="th" scope="row" data-label="Customer">{item?.customer_name}</TableCell>
                <TableCell component="th" scope="row" data-label="Address">{item?.pickup_details?.address}</TableCell>
                <TableCell data-label="Status">
                  {item?.payment_status === 1 || item?.payment_status === "1" ? (<Button className={`${styles.StatusBU} ${styles.DisaBU}`}>Paid</Button>) : (<Button className={`${styles.StatusBU}`}>Unpaid</Button>)}
                </TableCell>
                <TableCell component="th" scope="row" data-label="Contact No">{item?.pickup_details?.phone}</TableCell>
                <TableCell component="th" scope="row" data-label="Date">{dayjs(item?.pickup_date).format("DD/MM/YYYY")}</TableCell>
              </StyledMainTableRow>);
            })}

            {filteredData.length === 0 && (<TableRow>
              <TableCell colSpan={10} align="center">
                <div className={styles.EmptyState}>
                  <Inbox size={32} />
                  <p className={styles.EmptyStateTitle}>No bookings found</p>
                  <p className={styles.EmptyStateText}>Try a different date range or clear your search.</p>
                </div>
              </TableCell>
            </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredData?.length > 0 && (<div className={styles.PaginationWrap}>
        <TablePaginationComponent
          count={totalCount}
          colSpan={6}
          page={page}
          rowsPerPage={rowsPerPage}
          handleChangePage={paginationChangePage}
          handleChangeRowsPerPage={paginationChangeRowsPerPage}
        />
      </div>)}
    </div>

    <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AssignPopup setLoading={setLoading.bind(this)} modalClose={setFormOpen.bind(this)} bookingIds={selected} onSubmit={onAssign.bind(this)} setFormOpen={setFormOpen.bind(this)} />
    </Dialog>

    <Dialog open={driverFormOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
      <AddDriverForm setFormOpen={setDriverFormOpen.bind(this)} setAllData={null} setSnackbarOpen={setSnackbarOpen.bind(this)} setSnackbarMessage={setSnackbarMessage.bind(this)} setSnackbarType={setSnackbarType.bind(this)} setLoading={setLoading.bind(this)} editData={null} />
    </Dialog>

  </>);
};

export default withAdminAuth(BookingList);
